import React from 'react';
import { StepContainer } from '../StepContainer';
import { FormSection } from './form/formSection';
import { FormItem } from './form/formItem';

interface DemolitionStepProps {
  data: any;
  onChange: (data: any) => void;
  onNext: () => void;
  onBack: () => void;
}

export const DemolitionStep: React.FC<DemolitionStepProps> = ({
  data = {
    elementsToRemove: {},
    decaissement: {},
    toitureamiantee: {},
    demolitionLourde: {},
    evacuation: {}
  },
  onChange,
  onNext,
  onBack
}) => {
  const updateSection = (section: string, key: string, value: any) => {
    onChange({
      ...data,
      [section]: {
        ...data[section],
        [key]: value
      }
    });
  };

  return (
    <StepContainer
      title="Démolition"
      description="Détaillez les travaux de démolition nécessaires"
      onNext={onNext}
      onBack={onBack}
    >
      <div className="space-y-6">
        <FormSection 
          title="1. Éléments à déposer"
          description="Sélectionnez les éléments à retirer"
        >
          <div className="space-y-4">
            <FormItem
              label="Cloisons"
              value={data.elementsToRemove?.cloisons || { selected: false }}
              onChange={(value) => updateSection('elementsToRemove', 'cloisons', value)}
              withSurface
            />
            <FormItem
              label="Revêtements de sol"
              value={data.elementsToRemove?.sols || { selected: false }}
              onChange={(value) => updateSection('elementsToRemove', 'sols', value)}
              withSurface
            />
            <FormItem
              label="Faux plafonds"
              value={data.elementsToRemove?.fauxPlafonds || { selected: false }}
              onChange={(value) => updateSection('elementsToRemove', 'fauxPlafonds', value)}
              withSurface
            />
            <FormItem
              label="Équipements sanitaires"
              value={data.elementsToRemove?.sanitaires || { selected: false }}
              onChange={(value) => updateSection('elementsToRemove', 'sanitaires', value)}
              withQuantity
            />
            <FormItem
              label="Fenêtres et portes"
              value={data.elementsToRemove?.menuiseries || { selected: false }}
              onChange={(value) => updateSection('elementsToRemove', 'menuiseries', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="2. Décaissement de sol"
          description="Travaux de terrassement intérieur (rez-de-chaussée uniquement)"
        >
          <div className="space-y-4">
            <FormItem
              label="Décaissement léger (20 cm) et évacuation des déblais"
              value={data.decaissement?.leger || { selected: false }}
              onChange={(value) => updateSection('decaissement', 'leger', value)}
              withSurface
            />
            <FormItem
              label="Décaissement profond (40 cm ou plus) et évacuation des déblais"
              value={data.decaissement?.profond || { selected: false }}
              onChange={(value) => updateSection('decaissement', 'profond', value)}
              withSurface
            />
            <div className="mt-4 ml-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-medium text-gray-700 mb-3">Ne pas oublier :</p>
              <div className="space-y-4">
                <FormItem
                  label="Création d'un nouveau radier/dalle avec structure béton armé"
                  value={data.decaissement?.radier || { selected: false }}
                  onChange={(value) => updateSection('decaissement', 'radier', value)}
                  withSurface
                />
                <FormItem
                  label="Étanchéité / cuvelage de la surface décaissée"
                  value={data.decaissement?.etancheite || { selected: false }}
                  onChange={(value) => updateSection('decaissement', 'etancheite', value)}
                  withSurface
                />
                <FormItem
                  label="Correction enduit sur bas des murs"
                  value={data.decaissement?.enduit || { selected: false }}
                  onChange={(value) => updateSection('decaissement', 'enduit', value)}
                  withSurface
                />
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="3. Dépose de toiture amiantée"
          description="Dépose de toiture contenant de l'amiante"
        >
          <FormItem
            label="Toiture contenant de l'amiante"
            value={data.toitureamiantee?.amiante || { selected: false }}
            onChange={(value) => updateSection('toitureamiantee', 'amiante', value)}
            withSurface
          />
        </FormSection>

        <FormSection
          title="4. Démolition lourde"
          description="Travaux de démolition structurelle"
        >
          <div className="space-y-4">
            <FormItem
              label="Suppression de murs porteurs"
              value={data.demolitionLourde?.mursPorteurs || { selected: false }}
              onChange={(value) => updateSection('demolitionLourde', 'mursPorteurs', value)}
              withLength
            />
            <FormItem
              label="Ouverture de trémies"
              value={data.demolitionLourde?.tremies || { selected: false }}
              onChange={(value) => updateSection('demolitionLourde', 'tremies', value)}
              withQuantity
            />
            <FormItem
              label="Démolition de cheminées"
              value={data.demolitionLourde?.cheminees || { selected: false }}
              onChange={(value) => updateSection('demolitionLourde', 'cheminees', value)}
              withQuantity
            />
          </div>
        </FormSection>

        <FormSection
          title="5. Évacuation des gravats"
          description="Gestion des déchets de démolition"
        >
          <div className="space-y-4">
            <FormItem
              label="Déchets inertes"
              value={data.evacuation?.inertes || { selected: false }}
              onChange={(value) => updateSection('evacuation', 'inertes', value)}
              withVolume
            />
            <FormItem
              label="Déchets dangereux"
              value={data.evacuation?.dangereux || { selected: false }}
              onChange={(value) => updateSection('evacuation', 'dangereux', value)}
              withVolume
            />
          </div>
        </FormSection>
      </div>
    </StepContainer>
  );
};

export default DemolitionStep;
